(() => {
    const cartUtils = window.EEGCartUtils || {};
    const getSectionInnerHtml = cartUtils.getSectionInnerHtml || (() => '');
    const updateCartIconBubble = cartUtils.updateCartIconBubble || (() => {});
    const updateCartLiveRegion = cartUtils.updateCartLiveRegion || (() => {});

    const trapFocus = (container, elementToFocus) => {
        if (typeof window.trapFocus === 'function') {
            window.trapFocus(container, elementToFocus);
            return;
        }
        if (elementToFocus && typeof elementToFocus.focus === 'function') {
            elementToFocus.focus();
        }
    };

    const removeTrapFocus = (elementToFocus) => {
        if (typeof window.removeTrapFocus === 'function') {
            window.removeTrapFocus(elementToFocus);
            return;
        }
        if (elementToFocus && typeof elementToFocus.focus === 'function') {
            elementToFocus.focus();
        }
    };

    const getSectionsUrl = () => window.location.pathname || '/';

    // ========================================
    // Cart Drawer CSS safeguard (async styles; ensure ready before opening).
    // ========================================

    let cartDrawerStylesPromise = null;

    const getCartDrawerStylesSrc = () => {
        if (!document.body) return '';
        return document.body.getAttribute('data-cart-drawer-css-src') || '';
    };

    const getCartItemStylesSrc = () => {
        if (!document.body) return '';
        return document.body.getAttribute('data-cart-item-css-src') || '';
    };

    const markCartDrawerStylesReady = () => {
        if (!document.documentElement) return;
        document.documentElement.classList.add('eeg-cart-drawer-css-loaded');
    };

    const loadStylesheet = (src, dataAttr) => {
        if (!window.EEGAssetLoader || typeof window.EEGAssetLoader.loadStylesheetOnce !== 'function') {
            return Promise.reject(new Error('Cart drawer styles failed to load.'));
        }
        return window.EEGAssetLoader.loadStylesheetOnce(src, {
            dataAttr,
            media: 'all',
            setMediaOnLoad: { from: 'print', to: 'all' },
            errorMessage: 'Cart drawer styles failed to load.',
        });
    };

    const ensureCartDrawerStyles = () => {
        if (cartDrawerStylesPromise) return cartDrawerStylesPromise;

        const drawerSrc = getCartDrawerStylesSrc();
        if (!drawerSrc) {
            return Promise.reject(new Error('Cart drawer styles not found.'));
        }
        const itemSrc = getCartItemStylesSrc();

        cartDrawerStylesPromise = Promise.all([
            loadStylesheet(drawerSrc, 'data-eeg-cart-drawer-css'),
            loadStylesheet(itemSrc, 'data-eeg-cart-item-css'),
        ]).then(() => {
            markCartDrawerStylesReady();
        }).catch((error) => {
            cartDrawerStylesPromise = null;
            throw error;
        });

        return cartDrawerStylesPromise;
    };

    class CartDrawer extends HTMLElement {
        constructor() {
            super();
            this.drawer = this.querySelector('[data-cart-drawer]') || this;
            this.panel = this.querySelector('[data-cart-panel]');
            this.activeElement = null;
            this.openAfterStylesPromise = null;
            this.pendingOpenTriggeredBy = null;
            this.pendingRefreshPromise = null;
            this.pendingRenderState = null;
            this.redirectTimer = null;
            this.refreshTimeoutTimer = null;
            this.refreshTimedOut = false;

            this.addEventListener('keyup', (event) => {
                if (event.key !== 'Escape') return;
                this.close();
            });

            this.querySelectorAll('[data-cart-close]').forEach((button) => {
                button.addEventListener('click', (event) => {
                    event.preventDefault();
                    this.close();
                });
            });

            this.setHeaderCartIconAccessibility();

            if (this.hasAttribute('data-cart-drawer-shell') && this.drawer && this.drawer.classList.contains('is-open')) {
                this.open();
            }
        }

        setActiveElement(element) {
            this.activeElement = element;
        }

        getSectionsToRender() {
            return [
                {
                    id: 'eeg-cart-drawer',
                },
                {
                    id: 'eeg-cart-icon-bubble',
                },
                {
                    id: 'eeg-cart-live-region-text',
                },
            ];
        }

        setHeaderCartIconAccessibility() {
            const cartToggle = document.getElementById('cart-icon-bubble')
                || document.querySelector('[data-cart-toggle]');
            if (!cartToggle) return;

            cartToggle.setAttribute('aria-haspopup', 'dialog');
            if (this.panel && this.panel.id) {
                cartToggle.setAttribute('aria-controls', this.panel.id);
            }
            cartToggle.setAttribute('aria-expanded', 'false');

            cartToggle.addEventListener('click', (event) => {
                event.preventDefault();
                this.open(cartToggle);
            });

            cartToggle.addEventListener('keydown', (event) => {
                if (event.code.toUpperCase() !== 'SPACE') return;
                event.preventDefault();
                this.open(cartToggle);
            });
        }

        clearRedirectTimer() {
            if (!this.redirectTimer) return;
            window.clearTimeout(this.redirectTimer);
            this.redirectTimer = null;
        }

        showErrorAndRedirect(message) {
            const errorMessage = message || "Sorry, we couldn't load cart right now. Redirecting you to the cart page...";
            this.openShell();

            const loadingText = this.querySelector('.cart-drawer__loading-text');
            if (loadingText) {
                loadingText.textContent = errorMessage;
            }

            const errors = this.querySelector('[data-cart-errors]');
            if (errors) {
                errors.textContent = errorMessage;
                errors.hidden = false;
            }

            const fallbackUrl = (window.routes && window.routes.cart_url) || '/cart';
            this.clearRedirectTimer();
            this.redirectTimer = window.setTimeout(() => {
                window.location.href = fallbackUrl;
            }, 3000);
        }

        openShell(triggeredBy = null) {
            if (triggeredBy) {
                this.activeElement = triggeredBy;
            } else if (!this.activeElement) {
                this.activeElement = document.activeElement;
            }

            if (this.drawer) {
                this.drawer.classList.add('is-open');
                this.drawer.setAttribute('aria-hidden', 'false');
            }

            this.setToggleExpanded(true);
            if (document.body) {
                document.body.classList.add('overlay-open');
            }
        }

        open(triggeredBy = null) {
            const isAlreadyOpen = this.drawer && this.drawer.classList.contains('is-open');
            const isShell = this.hasAttribute('data-cart-drawer-shell');
            const stylesReady = document.documentElement
                ? document.documentElement.classList.contains('eeg-cart-drawer-css-loaded')
                : false;

            this.clearRedirectTimer();

            this.pendingOpenTriggeredBy = triggeredBy;
            this.openShell(triggeredBy);

            if (this.openAfterStylesPromise) return;
            if (isAlreadyOpen && !isShell && stylesReady && !this.pendingRenderState) return;

            this.openAfterStylesPromise = ensureCartDrawerStyles()
                .then(() => {
                    this.openAfterStylesPromise = null;
                    const nextTriggeredBy = this.pendingOpenTriggeredBy;
                    this.pendingOpenTriggeredBy = null;
                    this.openWithStyles(nextTriggeredBy);
                })
                .catch(() => {
                    this.openAfterStylesPromise = null;
                    this.pendingOpenTriggeredBy = null;
                    this.showErrorAndRedirect();
                });
        }

        openWithStyles(triggeredBy = null) {
            if (triggeredBy) {
                this.activeElement = triggeredBy;
            } else if (!this.activeElement) {
                this.activeElement = document.activeElement;
            }
            if (this.drawer) {
                this.drawer.classList.add('is-open');
                this.drawer.setAttribute('aria-hidden', 'false');
            }

            this.setToggleExpanded(true);
            if (document.body) {
                document.body.classList.add('overlay-open');
            }

            let focusSet = false;
            const handleTransitionEnd = (event) => {
                if (this.panel && event.target !== this.panel) return;
                if (focusSet) return;
                focusSet = true;
                this.focusDrawer();
            };

            if (this.drawer) {
                this.drawer.addEventListener('transitionend', handleTransitionEnd, { once: true });
            }
            window.setTimeout(() => {
                if (focusSet) return;
                focusSet = true;
                this.focusDrawer();
            }, 160);

            this.loadContentsIfNeeded();
        }

        loadContentsIfNeeded() {
            if (this.pendingRenderState) {
                const pendingState = this.pendingRenderState;
                this.pendingRenderState = null;
                this.updateSections(pendingState.sections, pendingState);
                return Promise.resolve(pendingState);
            }

            if (!this.hasAttribute('data-cart-drawer-shell')) return Promise.resolve(null);
            if (this.pendingRefreshPromise) return this.pendingRefreshPromise;

            this.refreshTimedOut = false;
            if (this.refreshTimeoutTimer) {
                window.clearTimeout(this.refreshTimeoutTimer);
                this.refreshTimeoutTimer = null;
            }
            const refreshTimeoutMs = 5000;
            this.refreshTimeoutTimer = window.setTimeout(() => {
                this.refreshTimedOut = true;
                this.showErrorAndRedirect();
            }, refreshTimeoutMs);

            this.pendingRefreshPromise = this.refresh()
                .then((cart) => {
                    if (this.refreshTimedOut) return null;
                    if (!cart) {
                        this.showErrorAndRedirect();
                    }
                    return cart;
                })
                .finally(() => {
                    if (this.refreshTimeoutTimer) {
                        window.clearTimeout(this.refreshTimeoutTimer);
                        this.refreshTimeoutTimer = null;
                    }
                    this.refreshTimedOut = false;
                    this.pendingRefreshPromise = null;
                });

            return this.pendingRefreshPromise;
        }

        close() {
            if (this.drawer) {
                this.drawer.classList.remove('is-open');
                this.drawer.setAttribute('aria-hidden', 'true');
            }
            this.setToggleExpanded(false);
            removeTrapFocus(this.activeElement);
            this.activeElement = null;
            this.unlockBody();
        }

        focusDrawer() {
            const focusContainer = this.panel || this.drawer;
            if (!focusContainer) return;
            const focusTarget = this.panel
                ? this.panel.querySelector('[data-cart-close]')
                : this.drawer.querySelector('[data-cart-close]');
            trapFocus(focusContainer, focusTarget || focusContainer);
        }

        setToggleExpanded(isExpanded) {
            const cartToggle = document.getElementById('cart-icon-bubble')
                || document.querySelector('[data-cart-toggle]');
            if (!cartToggle) return;
            cartToggle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
        }

        unlockBody() {
            if (!document.body) return;
            const otherOverlayOpen = document.querySelector('[data-search-overlay].is-open')
                || document.querySelector('[data-mobile-menu].is-open')
                || document.querySelector('[data-quick-add-modal].is-open');
            if (!otherOverlayOpen) {
                document.body.classList.remove('overlay-open');
            }
        }

        updateSections(sections, cart = null) {
            if (!sections || !sections['eeg-cart-drawer']) return;
            const doc = new DOMParser().parseFromString(sections['eeg-cart-drawer'], 'text/html');
            const sourceDrawer = doc.querySelector('[data-cart-drawer]');
            if (!sourceDrawer) return;

            const sourceHeader = sourceDrawer.querySelector('.cart-drawer__header');
            let targetHeader = this.querySelector('.cart-drawer__header');
            if (sourceHeader && this.panel) {
                const shouldReplaceHeader = targetHeader && targetHeader.hasAttribute('data-cart-header-shell');
                if (!targetHeader || shouldReplaceHeader) {
                    const nextHeader = sourceHeader.cloneNode(true);
                    if (targetHeader) {
                        targetHeader.replaceWith(nextHeader);
                    } else {
                        this.panel.insertBefore(nextHeader, this.panel.firstChild);
                    }
                    targetHeader = nextHeader;
                    targetHeader.querySelectorAll('[data-cart-close]').forEach((button) => {
                        button.addEventListener('click', (event) => {
                            event.preventDefault();
                            this.close();
                        });
                    });
                }
            }

            const sourceItems = sourceDrawer.querySelector('[data-cart-items]');
            const targetItems = this.querySelector('[data-cart-items]');
            if (sourceItems && targetItems) {
                targetItems.innerHTML = sourceItems.innerHTML;
            }

            const sourceFooter = sourceDrawer.querySelector('[data-cart-footer]');
            const targetFooter = this.querySelector('[data-cart-footer]');
            if (sourceFooter && targetFooter) {
                targetFooter.innerHTML = sourceFooter.innerHTML;
                if (sourceFooter.hasAttribute('hidden')) {
                    targetFooter.setAttribute('hidden', 'true');
                } else {
                    targetFooter.removeAttribute('hidden');
                }
            }

            const sourceCount = sourceDrawer.querySelector('[data-cart-item-count]');
            const targetCount = this.querySelector('[data-cart-item-count]');
            if (sourceCount && targetCount) {
                targetCount.textContent = sourceCount.textContent;
            }

            const countValue = cart && typeof cart.item_count === 'number'
                ? cart.item_count
                : (sourceCount ? parseInt(sourceCount.textContent || '0', 10) : 0);
            this.classList.toggle('is-empty', countValue === 0);
            if (this.drawer) {
                this.drawer.classList.toggle('is-empty', countValue === 0);
            }

            if (this.hasAttribute('data-cart-drawer-shell')) {
                this.removeAttribute('data-cart-drawer-shell');
            }

            updateCartIconBubble(sections);
            updateCartLiveRegion(sections);
        }

        renderContents(parsedState) {
            if (!parsedState || !parsedState.sections) return;
            this.pendingRenderState = parsedState;
            this.open();
        }

        refresh() {
            const cartRequest = fetch('/cart.js', {
                headers: {
                    Accept: 'application/json',
                },
                credentials: 'same-origin',
            }).then((response) => response.json());

            const drawerRequest = fetch(`${getSectionsUrl()}?section_id=eeg-cart-drawer`, {
                credentials: 'same-origin',
            }).then((response) => response.text());

            const iconRequest = fetch(`${getSectionsUrl()}?section_id=eeg-cart-icon-bubble`, {
                credentials: 'same-origin',
            }).then((response) => response.text());

            const liveRegionRequest = fetch(`${getSectionsUrl()}?section_id=eeg-cart-live-region-text`, {
                credentials: 'same-origin',
            }).then((response) => response.text());

            return Promise.all([cartRequest, drawerRequest, iconRequest, liveRegionRequest])
                .then(([cart, drawerHtml, iconHtml, liveRegionHtml]) => {
                    if (this.refreshTimedOut) return null;
                    const sections = {
                        'eeg-cart-drawer': drawerHtml,
                        'eeg-cart-icon-bubble': iconHtml,
                        'eeg-cart-live-region-text': liveRegionHtml,
                    };
                    this.updateSections(sections, cart);
                    return cart;
                })
                .catch(() => null);
        }
    }

    if (!customElements.get('cart-drawer')) {
        customElements.define('cart-drawer', CartDrawer);
    }

    const getDrawerInstance = () => document.querySelector('cart-drawer');

    window.EEGCartDrawer = {
        open: (triggeredBy) => {
            const drawer = getDrawerInstance();
            if (!drawer) return;
            drawer.open(triggeredBy);
        },
        close: () => {
            const drawer = getDrawerInstance();
            if (!drawer) return;
            drawer.close();
        },
        setActiveElement: (element) => {
            const drawer = getDrawerInstance();
            if (!drawer) return;
            drawer.setActiveElement(element);
        },
        getSectionsToRender: () => {
            const drawer = getDrawerInstance();
            if (!drawer || typeof drawer.getSectionsToRender !== 'function') return [];
            return drawer.getSectionsToRender();
        },
        refresh: () => {
            const drawer = getDrawerInstance();
            if (!drawer) return Promise.resolve(null);
            return drawer.refresh();
        },
        renderContents: (parsedState) => {
            const drawer = getDrawerInstance();
            if (!drawer) return;
            drawer.renderContents(parsedState);
        },
        updateSections: (sections, cart) => {
            const drawer = getDrawerInstance();
            if (!drawer) return;
            drawer.updateSections(sections, cart);
        },
    };
})();
